import { quantityToGrams } from '../../../utils/unitConversion';
import { calcNutrientsPer100g, fmtNut, kcalToKj, roundNutrient } from '../../../utils/nutritionUtils';
import { getMaterialEnglishName, getMaterialDutchName } from '../../../utils/materialName';

export const PRINTABLE_LABEL_STORAGE_PREFIX = 'tabliya-printable-label';

// Canonical default order for reorderable printable-label sections. Must match
// backend/models.py's DEFAULT_LABEL_SECTION_ORDER exactly. Excludes only the
// pinned title (product name) and fixed expiry footer.
export const DEFAULT_LABEL_SECTION_ORDER = ['netWeight', 'nutriScore', 'ingredients', 'allergens', 'nutrition', 'cookingMethods', 'storage', 'extraNotes'];

export const PRINTABLE_LABEL_COMPANY_DETAILS = {
  name: 'Tabliya B.V.',
  address: {
    en: 'Houthavenkade 120, 1014ZB, Amsterdam, Netherlands',
    nl: 'Houthavenkade 120, 1014ZB, Amsterdam, Nederland',
  },
};

// Keep only known keys, drop dupes, append any missing key at the end — same
// sanitization the backend applies, so a malformed/partial/legacy stored order
// (or a future new section type) never breaks rendering.
export function sanitizeSectionOrder(order) {
  if (!Array.isArray(order)) return [...DEFAULT_LABEL_SECTION_ORDER];
  const cleaned = [];
  const seen = new Set();
  for (const key of order) {
    if (DEFAULT_LABEL_SECTION_ORDER.includes(key) && !seen.has(key)) {
      cleaned.push(key);
      seen.add(key);
    }
  }
  for (const key of DEFAULT_LABEL_SECTION_ORDER) {
    if (!seen.has(key)) cleaned.push(key);
  }
  return cleaned;
}

export const EU_ALLERGENS = [
  'Gluten',
  'Crustaceans',
  'Eggs',
  'Fish',
  'Peanuts',
  'Soybeans',
  'Milk',
  'Tree nuts',
  'Celery',
  'Mustard',
  'Sesame seeds',
  'Sulphur dioxide and sulphites',
  'Lupin',
  'Molluscs',
];

const ALLERGEN_NL_MAP = {
  Gluten: 'Gluten',
  Crustaceans: 'Schaaldieren',
  Eggs: 'Eieren',
  Fish: 'Vis',
  Peanuts: "Pinda's",
  Soybeans: 'Soja',
  Milk: 'Melk',
  'Tree nuts': 'Noten',
  Celery: 'Selderij',
  Mustard: 'Mosterd',
  'Sesame seeds': 'Sesamzaad',
  'Sulphur dioxide and sulphites': 'Zwaveldioxide en sulfieten',
  Lupin: 'Lupine',
  Molluscs: 'Weekdieren',
};

const ALLERGEN_NORMALIZATION_RULES = [
  { pattern: /gluten/i, value: 'Gluten' },
  { pattern: /crustacean|shrimp|prawn|crab|lobster|crayfish/i, value: 'Crustaceans' },
  { pattern: /egg/i, value: 'Eggs' },
  { pattern: /fish|anchov|sardine|tuna|salmon|cod|mackerel/i, value: 'Fish' },
  { pattern: /peanut/i, value: 'Peanuts' },
  { pattern: /soy/i, value: 'Soybeans' },
  { pattern: /milk|dairy|lactose|whey|casein/i, value: 'Milk' },
  { pattern: /tree nut|almond|walnut|cashew|pistachio|hazelnut|pecan|brazil nut|macadamia|pine nut/i, value: 'Tree nuts' },
  { pattern: /celery|celeriac/i, value: 'Celery' },
  { pattern: /mustard/i, value: 'Mustard' },
  { pattern: /sesame|tahini/i, value: 'Sesame seeds' },
  { pattern: /sulphur dioxide|sulfite|sulphite/i, value: 'Sulphur dioxide and sulphites' },
  { pattern: /lupin/i, value: 'Lupin' },
  { pattern: /mollusc|mussel|clam|oyster|squid|octopus|scallop/i, value: 'Molluscs' },
];

export const PRINTABLE_LABEL_LOCALES = {
  en: {
    workspaceTitle: 'Printable Product Labels',
    previewTitle: 'English label',
    productName: 'Product name',
    netWeight: 'Net Weight',
    nutriScore: 'Nutri-Score',
    ingredients: 'Ingredients',
    allergens: 'Allergens',
    nutrition: 'Nutrition',
    nutritionPer100g: 'Nutrition per 100 g',
    nutritionPerServing: 'Nutrition per serving',
    preparation: 'Preparation',
    storage: 'Storage',
    manufacturer: 'Produced by',
    extraNotes: 'Extra notes',
    extraNotesTitle: 'Extra notes title',
    expiry: 'Best before',
    basisLabel: 'Label nutrition basis',
    basisPer100g: 'Per 100 g',
    basisPerServing: 'Per serving',
    nutritionLines: {
      energy: 'Energy',
      fat: 'Fat',
      saturatedFat: 'Saturated fat',
      carbohydrate: 'Carbohydrates',
      sugars: 'of which sugars',
      fiber: 'Fiber',
      protein: 'Protein',
      salt: 'Salt',
    },
  },
  nl: {
    workspaceTitle: 'Printbare Productlabels',
    previewTitle: 'Nederlands label',
    productName: 'Productnaam',
    netWeight: 'Nettogewicht',
    nutriScore: 'Nutri-Score',
    ingredients: 'Ingrediënten',
    allergens: 'Allergenen',
    nutrition: 'Voedingswaarden',
    nutritionPer100g: 'Voedingswaarden per 100 g',
    nutritionPerServing: 'Voedingswaarden per portie',
    preparation: 'Bereidingswijze',
    storage: 'Bewaren',
    manufacturer: 'Vervaardigd door',
    extraNotes: 'Extra opmerkingen',
    extraNotesTitle: 'Titel extra opmerkingen',
    expiry: 'Ten minste houdbaar tot',
    basisLabel: 'Basis voedingswaarden op label',
    basisPer100g: 'Per 100 g',
    basisPerServing: 'Per portie',
    nutritionLines: {
      energy: 'Energie',
      fat: 'Vetten',
      saturatedFat: 'Waarvan verzadigd',
      carbohydrate: 'Koolhydraten',
      sugars: 'Waarvan suikers',
      fiber: 'Vezels',
      protein: 'Eiwitten',
      salt: 'Zout',
    },
  },
};

const PRINTABLE_NUTRIENT_DEFINITIONS = [
  { key: 'energy', labelKey: 'energy', alwaysVisible: true, type: 'energy' },
  { key: 'fat', labelKey: 'fat', alwaysVisible: true, type: 'grams' },
  { key: 'saturatedFat', labelKey: 'saturatedFat', type: 'grams' },
  { key: 'carbohydrate', labelKey: 'carbohydrate', alwaysVisible: true, type: 'grams' },
  { key: 'sugars', labelKey: 'sugars', type: 'grams' },
  { key: 'fiber', labelKey: 'fiber', type: 'grams' },
  { key: 'protein', labelKey: 'protein', alwaysVisible: true, type: 'grams' },
  { key: 'salt', sourceKey: 'sodium', labelKey: 'salt', type: 'salt' },
];

function isMeaningfulValue(value) {
  return Number.isFinite(+value) && +value !== 0;
}

// EU salt declaration: salt (g) = sodium (mg) × 2.5 / 1000, rounded to 2 decimals.
function formatSaltG(sodiumG, locale) {
  const saltG = roundNutrient(sodiumG * 2.5, 2);
  const str = saltG.toFixed(2);
  return locale === 'nl' ? str.replace('.', ',') : str;
}

function formatNutritionValue(type, value, locale) {
  if (!Number.isFinite(+value)) return '—';

  if (type === 'energy') {
    return `${fmtNut(value, 0)} kcal / ${kcalToKj(value)} kJ`;
  }

  if (type === 'milligrams') {
    return `${fmtNut(value * 1000, 0)} mg`;
  }

  if (type === 'salt') {
    return `${formatSaltG(value, locale)} g`;
  }

  return `${fmtNut(value)} g`;
}

export function createMaterialsById(materials = []) {
  return materials.reduce((acc, material) => {
    acc[material.id] = material;
    return acc;
  }, {});
}

export function recipeHasFoodItems(recipe, materialsById = {}) {
  return (recipe?.items || []).some((item) => {
    const material = materialsById[item.material_id];
    return Boolean(material && !material.is_non_food);
  });
}

export function computeRecipeFoodWeightG(recipe, materialsById = {}) {
  return (recipe?.items || []).reduce((sum, item) => {
    const material = materialsById[item.material_id];
    if (!material || material.is_non_food) return sum;
    // Mirror the same filter used by nutrition.total: skip items with incomplete nutrition data
    // so numerator and denominator cover the same set of ingredients
    if (
      material.energy_per_100g == null ||
      material.protein_per_100g == null ||
      material.fat_per_100g == null ||
      material.carbohydrate_per_100g == null
    ) return sum;

    const unit = item.material_unit || item.unit || material.unit;
    return sum + quantityToGrams(item.quantity, unit);
  }, 0);
}

export function buildIngredientsText(recipe, materialsById = {}, locale = 'en') {
  const seen = new Set();
  const names = [];

  for (const item of recipe?.items || []) {
    const material = materialsById[item.material_id];
    if (!material || material.is_non_food) continue;

    const name = locale === 'nl'
      ? (getMaterialDutchName(item) || getMaterialDutchName(material) || getMaterialEnglishName(item) || getMaterialEnglishName(material))
      : (getMaterialEnglishName(item) || getMaterialEnglishName(material));
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;

    seen.add(key);
    names.push(name);
  }

  return names.join(', ');
}

function uniqSortedAllergens(values = []) {
  const unique = Array.from(new Set(values.filter(Boolean)));
  return unique.sort((a, b) => EU_ALLERGENS.indexOf(a) - EU_ALLERGENS.indexOf(b));
}

export function normalizeAllergenName(value) {
  const text = String(value || '').trim();
  if (!text) return null;

  const exact = EU_ALLERGENS.find((allergen) => allergen.toLowerCase() === text.toLowerCase());
  if (exact) return exact;

  const rule = ALLERGEN_NORMALIZATION_RULES.find(({ pattern }) => pattern.test(text));
  return rule?.value || null;
}

export function parseAllergenList(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return uniqSortedAllergens(value.map(normalizeAllergenName));
  }

  return uniqSortedAllergens(
    String(value)
      .replace(/^contains:\s*/i, '')
      .replace(/^bevat:\s*/i, '')
      .replace(/^may contain:\s*/i, '')
      .replace(/^kan sporen bevatten van:\s*/i, '')
      .split(',')
      .map((item) => normalizeAllergenName(
        item
          .replace(/^contains:\s*/i, '')
          .replace(/^bevat:\s*/i, '')
          .replace(/^may contain:\s*/i, '')
          .replace(/^kan sporen bevatten van:\s*/i, '')
      ))
  );
}

export function buildRecipeAllergenSummary(recipe, materialsById = {}) {
  const contains = [];
  const mayContain = [];

  for (const item of recipe?.items || []) {
    const material = materialsById[item.material_id];
    if (!material || material.is_non_food) continue;

    contains.push(...parseAllergenList(material.allergens));
    mayContain.push(...parseAllergenList(material.may_contain));
  }

  const uniqueContains = uniqSortedAllergens(contains);
  const uniqueMayContain = uniqSortedAllergens(
    mayContain.filter((allergen) => !uniqueContains.includes(allergen))
  );

  const enParts = [];
  if (uniqueContains.length > 0) {
    enParts.push(`Contains: ${uniqueContains.join(', ')}.`);
  }
  if (uniqueMayContain.length > 0) {
    enParts.push(`May contain: ${uniqueMayContain.join(', ')}.`);
  }

  const nlContains = uniqueContains.map((allergen) => ALLERGEN_NL_MAP[allergen] || allergen);
  const nlMayContain = uniqueMayContain.map((allergen) => ALLERGEN_NL_MAP[allergen] || allergen);
  const nlParts = [];
  if (nlContains.length > 0) {
    nlParts.push(`Bevat: ${nlContains.join(', ')}.`);
  }
  if (nlMayContain.length > 0) {
    nlParts.push(`Kan sporen bevatten van: ${nlMayContain.join(', ')}.`);
  }

  return {
    contains: uniqueContains,
    mayContain: uniqueMayContain,
    en: enParts.join(' '),
    nl: nlParts.join(' '),
  };
}

export function calculatePrintableLabelNutritionPer100g(nutrition, totalFoodWeightG) {
  if (!nutrition?.total) return null;
  return calcNutrientsPer100g(nutrition.total, totalFoodWeightG);
}

export function buildPrintableLabelInitialState(recipe, materialsById = {}) {
  const productNameEn = recipe?.name_en || recipe?.name_nl || recipe?.name || '';
  const productNameNl = recipe?.name_nl || recipe?.name || '';
  const ingredientsEn = buildIngredientsText(recipe, materialsById, 'en');
  const ingredientsNl = buildIngredientsText(recipe, materialsById, 'nl');
  const notes = recipe?.notes || '';
  const allergens = buildRecipeAllergenSummary(recipe, materialsById);
  const enExtraNotesTitle = PRINTABLE_LABEL_LOCALES.en.extraNotes;
  const nlExtraNotesTitle = PRINTABLE_LABEL_LOCALES.nl.extraNotes;

  return {
    en: {
      productName: productNameEn,
      netWeight: '',
      ingredients: ingredientsEn,
      allergens: allergens.en || recipe?.label_allergens_en || '',
      storage: '',
      extraNotes: notes,
      extraNotesTitle: enExtraNotesTitle,
    },
    nl: {
      productName: productNameNl,
      netWeight: '',
      ingredients: ingredientsNl,
      allergens: allergens.nl || recipe?.label_allergens_nl || '',
      storage: '',
      extraNotes: notes,
      extraNotesTitle: nlExtraNotesTitle,
    },
    expiry: '',
    nutritionBasis: 'per100g',
    cookingMethods: [],
    sectionOrder: { en: [...DEFAULT_LABEL_SECTION_ORDER], nl: [...DEFAULT_LABEL_SECTION_ORDER] },
  };
}

export function mergePrintableLabelState(initialState, savedState) {
  if (!savedState || typeof savedState !== 'object') return initialState;

  const savedEn = savedState.en || {};
  const savedNl = savedState.nl || {};
  const mergedEn = {
    ...initialState.en,
    ...savedEn,
    allergens: savedEn.allergens?.trim() ? savedEn.allergens : initialState.en.allergens,
  };
  const mergedNl = {
    ...initialState.nl,
    ...savedNl,
    allergens: savedNl.allergens?.trim() ? savedNl.allergens : initialState.nl.allergens,
  };
  const savedSectionOrder = savedState.sectionOrder || {};

  return {
    ...initialState,
    ...savedState,
    en: mergedEn,
    nl: mergedNl,
    cookingMethods: Array.isArray(savedState.cookingMethods)
      ? savedState.cookingMethods.map((method, index) => createCookingMethod(method?.id ?? index + 1, method || {}))
      : initialState.cookingMethods,
    sectionOrder: {
      en: sanitizeSectionOrder(savedSectionOrder.en ?? initialState.sectionOrder.en),
      nl: sanitizeSectionOrder(savedSectionOrder.nl ?? initialState.sectionOrder.nl),
    },
  };
}

export function buildPrintableLabelStorageKey(recipeId) {
  return `${PRINTABLE_LABEL_STORAGE_PREFIX}:${recipeId}`;
}

export function calculatePrintableLabelNutritionData(nutrition, totalFoodWeightG, portionWeightG) {
  if (!nutrition?.total) return { perServing: null, per100g: null };

  const perPortion = nutrition.perServing ?? nutrition.total;
  const servingWeight = parseFloat(portionWeightG);
  const useServingWeight = Number.isFinite(servingWeight) && servingWeight > 0;

  return {
    perServing: perPortion,
    // When the user has entered a serving size, use the same formula as the Nutrition Facts panel
    // (perPortion / portionWeightG * 100) so both views always agree.
    // Fall back to total-ingredient weight only when no serving size is entered.
    per100g: useServingWeight
      ? calcNutrientsPer100g(perPortion, servingWeight)
      : calcNutrientsPer100g(nutrition.total, totalFoodWeightG),
  };
}

export function getPrintableNutritionHeading(locale, nutritionBasis, servingLabel = '') {
  const copy = PRINTABLE_LABEL_LOCALES[locale] || PRINTABLE_LABEL_LOCALES.en;

  if (nutritionBasis === 'perServing') {
    return servingLabel
      ? `${copy.nutritionPerServing} (${servingLabel})`
      : copy.nutritionPerServing;
  }

  return copy.nutritionPer100g;
}

export function formatPrintableNutrition(locale, nutrients, nutritionBasis, servingLabel = '') {
  if (!nutrients) {
    return {
      heading: getPrintableNutritionHeading(locale, nutritionBasis, servingLabel),
      rows: [],
    };
  }

  const translations = PRINTABLE_LABEL_LOCALES[locale]?.nutritionLines || PRINTABLE_LABEL_LOCALES.en.nutritionLines;

  const rows = PRINTABLE_NUTRIENT_DEFINITIONS
    .filter((definition) => {
      const value = nutrients[definition.sourceKey || definition.key];
      return definition.alwaysVisible ? Number.isFinite(+value) : isMeaningfulValue(value);
    })
    .map((definition) => ({
      key: definition.key,
      label: translations[definition.labelKey],
      value: formatNutritionValue(definition.type, nutrients[definition.sourceKey || definition.key], locale),
    }));

  return {
    heading: getPrintableNutritionHeading(locale, nutritionBasis, servingLabel),
    rows,
  };
}

export function createCookingMethod(id, overrides = {}) {
  return {
    id,
    iconKey: 'pan',
    text_en: '',
    text_nl: '',
    ...overrides,
  };
}

export function buildLabelDownloadFileName(recipeName, locale) {
  const safeName = (recipeName || 'recipe')
    .trim()
    .replace(/[^\w-]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return `${safeName || 'recipe'}_product_label_${locale}.png`;
}
